import React from "react";
import { Box, Chip, Tooltip, Typography } from "@material-ui/core";
import StarIcon from "@material-ui/icons/Star";
import CategoryIcon from "@material-ui/icons/Category";

const Q_COLORS = {
  Q1: "#2e7d32",
  Q2: "#1976d2",
  Q3: "#f57c00",
  Q4: "#d32f2f",
};

const UNGROUPED_KEY = "__ungrouped__";

function groupCategoriesByArea(areasList, categoriesList) {
  const groups = new Map();
  for (const area of areasList || []) {
    groups.set(area.id, { area, items: [] });
  }
  const ungrouped = { area: { id: 0, title: "Khác" }, items: [], key: UNGROUPED_KEY };

  for (const cat of categoriesList || []) {
    const bucket = groups.get(cat.areaId);
    if (bucket) {
      bucket.items.push(cat);
    } else {
      ungrouped.items.push(cat);
    }
  }
  const result = Array.from(groups.values()).filter((g) => g.items.length > 0);
  // Areas without categories: still show them so the user knows the journal is tagged with that area.
  for (const g of groups.values()) {
    if (g.items.length === 0) result.push(g);
  }
  if (ungrouped.items.length > 0) result.push(ungrouped);
  return result;
}

function CategoryChip({ category }) {
  const qColor = Q_COLORS[category.quartile] || "#607d8b";
  const label = category.quartile
    ? `${category.title} · ${category.quartile}`
    : category.title;

  return (
    <Box display="inline-flex" alignItems="center" style={{ marginRight: 6, marginBottom: 6 }}>
      <Chip
        size="small"
        label={label}
        style={{
          backgroundColor: qColor,
          color: "white",
          fontWeight: 600,
          height: 26,
        }}
      />
      {category.isTop5Pct && (
        <Tooltip title="Top 5% theo SJR trong phân ngành cùng năm">
          <Chip
            size="small"
            icon={<StarIcon style={{ color: "#fff", fontSize: 16 }} />}
            label="Top 5%"
            style={{
              marginLeft: 4,
              height: 26,
              backgroundColor: "#fbc02d",
              color: "#3e2723",
              fontWeight: 700,
              border: "1.5px solid #f57f17",
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

function JournalAreasCategoriesPanel({ areasList, categoriesList, dense }) {
  const hasAny =
    (areasList && areasList.length > 0) || (categoriesList && categoriesList.length > 0);

  const groups = React.useMemo(
    () => groupCategoriesByArea(areasList, categoriesList),
    [areasList, categoriesList]
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={1}>
        <CategoryIcon style={{ color: "#1565c0", marginRight: 6 }} />
        <Typography
          variant={dense ? "subtitle2" : "subtitle1"}
          style={{ fontWeight: 700, color: "#1565c0" }}
        >
          Lĩnh vực & Phân ngành (SCImago)
        </Typography>
      </Box>

      {!hasAny ? (
        <Typography variant="body2" color="textSecondary">
          Chưa có thông tin lĩnh vực / phân ngành cho tạp chí này.
        </Typography>
      ) : (
        <Box
          display="grid"
          gridGap={10}
          style={{
            gridTemplateColumns: dense
              ? "1fr"
              : "repeat(auto-fit, minmax(320px, 1fr))",
          }}
        >
          {groups.map((group, idx) => (
            <Box
              key={group.area.id || group.key || idx}
              style={{
                backgroundColor: "#f5f8ff",
                border: "1px solid #d0dcf5",
                borderRadius: 10,
                padding: 10,
              }}
            >
              <Box display="flex" alignItems="center" mb={1} flexWrap="wrap">
                <Typography
                  variant="body2"
                  style={{ fontWeight: 700, color: "#1a237e", marginRight: 8 }}
                >
                  {group.area.title || "Khác"}
                </Typography>
                <Chip
                  size="small"
                  label={`${group.items.length} phân ngành`}
                  style={{
                    height: 20,
                    fontSize: 11,
                    backgroundColor: "#e3eaff",
                    color: "#1a237e",
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Box display="flex" flexWrap="wrap">
                {group.items.length === 0 ? (
                  <Typography variant="caption" color="textSecondary">
                    (Chưa có phân ngành nào liên kết)
                  </Typography>
                ) : (
                  group.items.map((cat) => (
                    <CategoryChip
                      key={`${group.area.id}-${cat.categoryId}`}
                      category={cat}
                    />
                  ))
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default JournalAreasCategoriesPanel;
